<template>
  <tbe-accordion-item
    class="tbe-option"
    :class="{ 'tbe-option--active': !isCollapse }"
    :id="option.id"
    @click:trigger="handleClickOption"
  >
    <template v-slot:tbe-accordion-trigger>
      <div class="tbe-option__summary">
        <div class="tbe-option__title-block">
          <h6 class="tbe-option__tour-title">{{ booking.product.title }}</h6>
          <h4 class="tbe-option__title">#{{ index + 1 }} of {{ total }}: {{ option.title }}</h4>
        </div>
        <div class="tbe-option__total-price-block">
          <div class="tbe-option__total-price tbe-option-total-price">
            <span class="tbe-option-total-price__label">Total price</span>
            <span class="tbe-option-total-price__price">
              {{ generalSettings.currency.value }}&nbsp;{{ price.total }}
            </span>
            <small class="tbe-option-total-price__fees-info">All taxes and fees included</small>
          </div>
        </div>
        <div class="tbe-option__toggle-block">
          <button class="tbe-option__toggle" :class="{ 'tbe-option__toggle--active': !isCollapse }">
            <img v-if="!isCollapse" src="../assets/check.svg" alt="Check" />
            <img v-else src="../assets/arrow-down-thick.svg" alt="Arrow Down" />
          </button>
        </div>
      </div>
    </template>
    <template v-slot:tbe-accordion-content>
      <div class="tbe-option__body">
        <div class="tbe-option__content">
          <div v-if="option.description" class="tbe-option__row">
            <div class="tbe-option__column">
              <div class="tbe-option__description tbe-option-description">
                <!--<h5 class="tbe-option-description__title">Description</h5>-->
                <tbe-read-more
                  class="tbe-option-description__content"
                  v-model="isReadMore"
                  :text="option.description"
                  :word-count="media.mobile ? 15 : 50"
                />
              </div>
              <hr class="tbe-divider tbe-my-3" />
            </div>
          </div>
          <div class="tbe-option__row">
            <div class="tbe-option__column">
              <div class="tbe-option__languages tbe-option-languages">
                <h5 class="tbe-option-languages__title">Language:</h5>
                <tbe-toggle
                  v-if="languages.length > 1"
                  class="tbe-option-languages__toggle"
                  :group="`option-${option.id}-languages`"
                  name="languages"
                  item-text="value"
                  :items="languages"
                  v-model="language"
                  :item-width="'80px'"
                  :full-width="false"
                  :options="{ size: { fontSize: media.mobile ? 12 : 14 } }"
                />
                <span v-else>{{ language }}</span>
              </div>
              <hr class="tbe-divider tbe-my-3" />
            </div>
            <div class="tbe-option__column" />
          </div>
          <div class="tbe-option__row">
            <div class="tbe-option__column">
              <div class="tbe-option__start-times tbe-option-start-times">
                <h5 class="tbe-option-start-times__title">Select a starting time:</h5>
                <tbe-toggle
                  v-if="startTimes.length > 1"
                  class="tbe-option-start-times__toggle"
                  :group="`option-${option.id}-start-times`"
                  name="start-times"
                  item-value="id"
                  item-text="time"
                  :items="startTimes"
                  v-model="startTime.id"
                  disabled-item-value="is_expired"
                  :item-width="'80px'"
                  :full-width="true"
                  :options="{ size: { fontSize: media.mobile ? 12 : 14 } }"
                />
                <span v-else>{{ startTime.time }}</span>
              </div>
              <hr v-if="media.mobile" class="tbe-divider tbe-my-3" />
            </div>
            <div class="tbe-option__column">
              <tbe-price-breakdown
                class="tbe-option__price-breakdown"
                :amount-people="amountPeople"
                :price="price"
              />
            </div>
          </div>
        </div>
        <div class="tbe-option__footer">
          <div class="tbe-option__row tbe-align-items-center">
            <div class="tbe-option__column">
              <div class="tbe-cancellation-message">
                Cancel before {{ startTime.time }} on <br v-if="media.desktop" />
                {{ $filters.dateFormat(previousDate, 'PP') }} for a refund.
              </div>
            </div>
            <div class="tbe-option__column">
              <div class="tbe-reserve-now-pay-later-info">
                You can reserve now & pay later <br v-if="media.desktop" />
                with this activity option.
              </div>
            </div>
            <div class="tbe-option__column" />
            <div class="tbe-option__column">
              <tbe-button variant="primary" @click.prevent="handleClickOrder">
                Add to cart
              </tbe-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </tbe-accordion-item>
</template>

<script>
  import { computed, reactive, ref, toRefs, watch } from 'vue'
  import { useStore } from 'vuex'
  import { addDays } from 'date-fns'

  import { RsToggle as tbeToggle, RsButton as tbeButton } from 'common/components'
  import { tbeAccordionItem } from 'common/plugins/tbe-accordion'
  import { useMedia } from 'common/plugins/media'

  import { VIEWS } from '@/config'

  import useDataLayer, { DATA_LAYER_EVENTS } from '@/hooks/useDataLayer'

  import tbePriceBreakdown from '@/components/tbePriceBreakdown'

  export default {
    name: 'tbeOption',

    components: {
      tbeAccordionItem,
      tbePriceBreakdown,
      tbeToggle,
      tbeButton
    },

    props: {
      index: Number,
      total: Number,
      option: {
        type: Object,
        required: true
      }
    },

    setup(props) {
      const { option } = toRefs(props)

      const store = useStore()
      const media = useMedia()
      const dataLayer = useDataLayer()

      const isReadMore = ref(false)

      const language = ref()
      const startTime = reactive({ id: '', time: '' })
      const price = reactive({ id: '', toPerson: 0, total: 0 })

      const booking = computed(() => store.state.booking)
      const generalSettings = computed(() => store.state.app.generalSettings)

      const amountPeople = computed(() => store.state.booking.order.amountPeople)
      const date = computed(() => store.state.booking.order.date)
      const previousDate = computed(() => addDays(date.value, -1))

      const isCollapse = computed(() => {
        if (!booking.value.option) return true
        return booking.value.option.id !== option.value.id
      })

      const languages = computed(() => {
        const { guide_languages: guideLanguages } = option.value
        return guideLanguages.map((value) => ({ value }))
      })

      const selectFirstLanguage = ([firstLanguage]) => {
        language.value = firstLanguage.value
      }
      watch(languages, selectFirstLanguage, { immediate: true })

      const startTimes = computed(() => {
        const { schedule } = option.value
        const { weekly_available_slot: weeklyAvailableSlot } = schedule
        return weeklyAvailableSlot.times
      })

      const selectFirstStartTime = (startTimes) => {
        const availableStartTime = startTimes.find(({ is_expired }) => !is_expired)
        if (!availableStartTime) return

        startTime.id = availableStartTime.id
        startTime.time = availableStartTime.time
      }
      watch(startTimes, selectFirstStartTime, { immediate: true })

      const changeStartTime = (newStartTimeId) => {
        const { time } = startTimes.value.find(({ id }) => newStartTimeId === id)
        startTime.time = time
      }
      watch(() => startTime.id, changeStartTime)

      const calcPrice = (newOption) => {
        const { price_tier_slot: priceTierSlot } = newOption.price
        price.id = priceTierSlot.id
        price.toPerson = priceTierSlot.price
        price.total = amountPeople.value * priceTierSlot.price
      }
      watch(option, calcPrice, { immediate: true })

      const updateOrder = (payload) => store.dispatch('booking/updateOrder', payload)
      const setView = (payload) => store.dispatch('app/setView', payload)
      const handleClickOrder = () => {
        const {
          id: optionId,
          schedule: { id: scheduleId },
          price: { id: priceId }
        } = option.value
        const order = { optionId, scheduleId, priceId, price, startTime, language: language.value }
        updateOrder(order)

        dataLayer.clearEcommerce()
        dataLayer.pushEcommerce(DATA_LAYER_EVENTS.ECOMMERCE.ADD_TO_CART)

        setView(VIEWS.ORDER)
      }

      const handleClickOption = () => store.dispatch('booking/setOption', option)

      return {
        isReadMore,
        isCollapse,
        media,
        booking,
        generalSettings,
        amountPeople,
        language,
        languages,
        startTime,
        startTimes,
        price,
        previousDate,
        handleClickOrder,
        handleClickOption
      }
    }
  }
</script>

<style lang="scss" scoped>
  .tbe-option {
    border: 2px solid #dcdfe4;
    margin-bottom: 32px;
    background-color: #fff;
    transition: 0.2s border-color linear;

    &--active {
      border-color: var(--rs-accent-color);
    }

    &:hover {
      border-color: var(--rs-accent-color);
      cursor: pointer;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &__row {
      display: flex;
      flex-wrap: wrap;
      margin: 0 -15px;
    }
    &__column {
      flex: 1;
      padding: 0 15px;

      @media screen and (max-width: 768px) {
        width: 100%;
        flex-basis: 100%;
      }
    }

    &__tour-title {
      font-size: 14px;
      margin-bottom: 10px;
      margin-top: 0;
      font-weight: 500;

      @media screen and (max-width: 768px) {
        font-size: 12px;
      }
    }

    &__title {
      font-size: 22px;
      margin-bottom: 10px;
      font-weight: 500;
      line-height: 1.2;

      @media screen and (max-width: 768px) {
        font-size: 18px;
      }

      @media screen and (max-width: 420px) {
        font-size: 16px;
      }
    }

    &__summary {
      display: grid;
      grid-template-columns: 2fr 1fr 34px;
      gap: 15px;
      padding: 15px;
    }

    &__toggle-block {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__content {
      border-top: 1px solid #ebeef1;
      padding: 15px;
    }

    &__toggle {
      width: 34px;
      height: 34px;
      background: var(--rs-accent-color);
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 3px;
      box-shadow: 0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 24%);
      cursor: pointer;

      &--active {
        border-radius: 50%;
        background-color: #dcdfe4;
        box-shadow: none;
      }

      img {
        display: block;
        width: 100%;
        max-width: 20px;
        height: auto;
      }
    }

    &__footer {
      padding: 15px;
      background-color: #ebeef1;
    }
  }

  .tbe-option-description {
    &__title {
      font-size: 16px;
      margin-bottom: 5px;
      line-height: 1;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }
    &__content {
      font-size: 14px;
      line-height: 1.4;
      margin: 0;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }
  }

  .tbe-option-languages {
    &__title {
      font-size: 16px;
      padding-bottom: 5px;
      line-height: 1;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }
    &__toggle {
      border: none;
      padding: 0;
    }
  }

  .tbe-option-start-times {
    &__title {
      font-size: 16px;
      padding-bottom: 5px;
      line-height: 1;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }
    &__toggle {
      border: none;
      padding: 0;
    }
  }

  .tbe-option-total-price {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;

    &__label {
      font-size: 18px;
      margin-bottom: 5px;

      @media screen and (max-width: 768px) {
        font-size: 14px;
      }
    }

    &__price {
      font-size: 32px;
      font-weight: 600;
      margin-bottom: 5px;

      @media screen and (max-width: 768px) {
        font-size: 24px;
      }
    }

    &__fees-info {
      color: #007850;
      font-size: 14px;
      text-align: right;
      line-height: 1.2;

      @media screen and (max-width: 768px) {
        font-size: 12px;
      }
    }
  }

  .tbe-cancellation-message,
  .tbe-reserve-now-pay-later-info {
    line-height: 1.4;
    font-weight: 500;
    font-size: 14px;

    @media screen and (max-width: 768px) {
      margin-bottom: 10px;
      font-size: 12px;
    }
  }

  .slide-enter-active {
    transition-duration: 1s;
    transition-timing-function: ease-in;
  }

  .slide-leave-active {
    transition-duration: 1s;
    transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  }

  .slide-enter-to,
  .slide-leave {
    max-height: 1000px;
    overflow: hidden;
  }

  .slide-enter,
  .slide-leave-to {
    overflow: hidden;
    max-height: 0;
  }
</style>
